export interface ColumnType {
  type: 'currency' | 'percentage' | 'number' | 'date' | 'email' | 'phone' | 'text'
  confidence: number
  currency?: string
  locale?: string
  decimals?: number
  dateFormat?: string
}

export interface SmartFormatterOptions {
  defaultCurrency?: string
  defaultLocale?: string
  useLLM?: boolean
}

// Currency keywords for fuzzy matching
const CURRENCY_KEYWORDS = [
  'price', 'cost', 'amount', 'total', 'subtotal', 'fee', 'charge', 'revenue', 'income', 'profit',
  'salary', 'wage', 'payment', 'pay', 'commission', 'bonus', 'budget', 'expense', 'spend',
  'value', 'worth', 'bill', 'invoice', 'refund', 'credit', 'debit', 'balance', 'deposit',
  'withdrawal', 'transfer', 'loan', 'mortgage', 'rent', 'tax', 'discount', 'markup',
  'sales', 'purchase', 'buy', 'sell', 'sold', 'bought', 'cash', 'money', 'dollar', 'euro',
  'pound', 'yen', 'currency', 'financial', 'accounting', 'payroll', 'earnings', 'proceeds'
]

// Percentage keywords
const PERCENTAGE_KEYWORDS = [
  'percent', 'percentage', 'rate', 'ratio', 'margin', 'markup', 'discount', 'tax',
  'interest', 'growth', 'change', 'increase', 'decrease', 'share', 'portion',
  'commission', 'fee', 'roi', 'return', 'yield', 'efficiency', 'completion',
  'progress', 'score', 'grade', 'rating', 'conversion', 'click', 'open', 'bounce'
]

// Date keywords
const DATE_KEYWORDS = [
  'date', 'time', 'timestamp', 'created', 'updated', 'modified', 'published',
  'scheduled', 'deadline', 'due', 'start', 'end', 'begin', 'finish', 'expire',
  'birth', 'hire', 'hired', 'join', 'joined', 'leave', 'left', 'termination', 'anniversary', 'year',
  'month', 'day', 'hour', 'minute', 'second', 'when', 'occurred', 'happened'
]

// Email keywords
const EMAIL_KEYWORDS = [
  'email', 'mail', 'contact', 'address', 'user', 'account', 'login', 'username'
]

// Phone keywords
const PHONE_KEYWORDS = [
  'phone', 'mobile', 'cell', 'telephone', 'number', 'contact', 'call', 'sms'
]

// Words that should NEVER be considered currency despite fuzzy matching
const CURRENCY_EXCLUSIONS = [
  'age', 'page', 'stage', 'cage', 'rage', 'name', 'game', 'same', 'came', 'fame',
  'time', 'line', 'mine', 'fine', 'nine', 'wine', 'size', 'life', 'wife', 'safe',
  'code', 'node', 'mode', 'rode', 'vote', 'note', 'quote', 'wrote', 'hope', 'scope',
  'type', 'pipe', 'wipe', 'file', 'pile', 'mile', 'tile', 'while', 'style', 'smile',
  'id', 'key', 'ref', 'row', 'col', 'max', 'min', 'avg', 'sum', 'count', 'index',
  'level', 'rank', 'position', 'order', 'sequence', 'step', 'phase', 'status'
]

class SmartFormatter {
  private options: SmartFormatterOptions
  private locale: string
  private currency: string

  constructor(options: SmartFormatterOptions = {}) {
    this.options = {
      defaultCurrency: 'USD',
      defaultLocale: 'en-US',
      useLLM: false,
      ...options
    }
    this.locale = this.options.defaultLocale!
    this.currency = this.options.defaultCurrency!
  }

  /**
   * Detect column type using fuzzy logic
   */
  private detectTypeByKeywords(columnName: string, sampleValues: any[]): ColumnType {
    const name = columnName.toLowerCase().trim()
    
    // Check for excluded words first
    if (CURRENCY_EXCLUSIONS.includes(name)) {
      // Skip currency detection for excluded words
      return this.detectNonCurrencyType(name, sampleValues)
    }
    
    // Check for currency indicators
    const currencyScore = this.calculateKeywordScore(name, CURRENCY_KEYWORDS)
    if (currencyScore > 0.5) { // Increased threshold
      // Double-check with data analysis
      if (this.looksLikeCurrency(sampleValues)) {
        return {
          type: 'currency',
          confidence: currencyScore,
          currency: this.currency,
          locale: this.locale,
          decimals: this.determineCurrencyDecimals(sampleValues)
        }
      }
    }

    return this.detectNonCurrencyType(name, sampleValues)
  }

  /**
   * Detect non-currency types
   */
  private detectNonCurrencyType(name: string, sampleValues: any[]): ColumnType {
    // Check for percentage indicators
    const percentageScore = this.calculateKeywordScore(name, PERCENTAGE_KEYWORDS)
    if (percentageScore > 0.3) {
      return {
        type: 'percentage',
        confidence: percentageScore,
        decimals: this.determinePercentageDecimals(sampleValues)
      }
    }

    // Check for date indicators
    const dateScore = this.calculateKeywordScore(name, DATE_KEYWORDS)
    
    // Debug for hire_date
    if (name === 'hire_date') {
      console.log('hire_date detection:', {
        name,
        dateScore,
        matches: DATE_KEYWORDS.filter(k => name.includes(k) || k.includes(name.split('_')[0])),
        sample: sampleValues.slice(0, 2)
      })
    }
    
    if (dateScore > 0.3) {
      return {
        type: 'date',
        confidence: dateScore
      }
    }

    // Check for email indicators
    const emailScore = this.calculateKeywordScore(name, EMAIL_KEYWORDS)
    if (emailScore > 0.3 && this.looksLikeEmail(sampleValues)) {
      return {
        type: 'email',
        confidence: emailScore
      }
    }

    // Check for phone indicators
    const phoneScore = this.calculateKeywordScore(name, PHONE_KEYWORDS)
    if (phoneScore > 0.3 && this.looksLikePhone(sampleValues)) {
      return {
        type: 'phone',
        confidence: phoneScore
      }
    }

    // Check if it's numeric
    if (this.isNumericColumn(sampleValues)) {
      return {
        type: 'number',
        confidence: 0.8,
        decimals: this.determineNumberDecimals(sampleValues)
      }
    }

    // Default to text
    return {
      type: 'text',
      confidence: 0.5
    }
  }

  /**
   * Calculate keyword matching score using fuzzy logic
   */
  private calculateKeywordScore(text: string, keywords: string[]): number {
    let maxScore = 0
    
    for (const keyword of keywords) {
      // Exact match
      if (text === keyword) {
        maxScore = Math.max(maxScore, 1.0)
        continue
      }
      
      // Contains keyword
      if (text.includes(keyword)) {
        maxScore = Math.max(maxScore, 0.8)
        continue
      }
      
      // Partial match at start
      if (text.startsWith(keyword) || keyword.startsWith(text)) {
        maxScore = Math.max(maxScore, 0.6)
        continue
      }
      
      // Fuzzy match (simple edit distance) - increased threshold
      const similarity = this.calculateSimilarity(text, keyword)
      if (similarity > 0.85) { // Increased from 0.7 to 0.85
        maxScore = Math.max(maxScore, similarity * 0.4) // Reduced weight
      }
    }
    
    return maxScore
  }

  /**
   * Simple similarity calculation
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2
    const shorter = str1.length > str2.length ? str2 : str1
    
    if (longer.length === 0) return 1.0
    
    const distance = this.levenshteinDistance(longer, shorter)
    return (longer.length - distance) / longer.length
  }

  /**
   * Calculate Levenshtein distance
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null))
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,     // deletion
          matrix[j - 1][i] + 1,     // insertion
          matrix[j - 1][i - 1] + indicator  // substitution
        )
      }
    }
    
    return matrix[str2.length][str1.length]
  }

  /**
   * Check if values look like currency based on data analysis
   */
  private looksLikeCurrency(values: any[]): boolean {
    const numericValues = values.filter(v => typeof v === 'number' || (!isNaN(parseFloat(v)) && isFinite(v)))
    
    if (numericValues.length === 0) return false
    
    // Check for currency-like patterns
    const avgValue = numericValues.reduce((sum, v) => {
      const num = typeof v === 'number' ? v : parseFloat(v)
      return sum + num
    }, 0) / numericValues.length
    
    // Currency values are typically larger than personal attributes like age
    // Age is typically 0-120, currency is typically much larger
    return avgValue > 200 || numericValues.some(v => {
      const num = typeof v === 'number' ? v : parseFloat(v)
      return num > 200
    })
  }

  /**
   * Check if values look like email addresses
   */
  private looksLikeEmail(values: any[]): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const validEmails = values.filter(v => 
      typeof v === 'string' && emailPattern.test(v)
    ).length
    
    return validEmails / values.length > 0.5
  }

  /**
   * Check if values look like phone numbers
   */
  private looksLikePhone(values: any[]): boolean {
    const phonePattern = /^[\+]?[1-9][\d\s\-\(\)\.]{7,15}$/
    const validPhones = values.filter(v => 
      typeof v === 'string' && phonePattern.test(v.replace(/\s/g, ''))
    ).length
    
    return validPhones / values.length > 0.5
  }

  /**
   * Check if column contains numeric values
   */
  private isNumericColumn(values: any[]): boolean {
    const numericValues = values.filter(v => 
      typeof v === 'number' || (!isNaN(parseFloat(v)) && isFinite(v))
    ).length
    
    return numericValues / values.length > 0.7
  }

  /**
   * Determine appropriate decimal places for currency
   */
  private determineCurrencyDecimals(values: any[]): number {
    const numbers = values.filter(v => typeof v === 'number' || !isNaN(parseFloat(v)))
    
    if (numbers.length === 0) return 2
    
    // Check if any values have decimals
    const hasDecimals = numbers.some(v => {
      const num = typeof v === 'number' ? v : parseFloat(v)
      return num % 1 !== 0
    })
    
    if (!hasDecimals) return 0
    
    // For large numbers, use 0 decimals
    const avgValue = numbers.reduce((sum, v) => {
      const num = typeof v === 'number' ? v : parseFloat(v)
      return sum + num
    }, 0) / numbers.length
    
    return avgValue > 1000 ? 0 : 2
  }

  /**
   * Determine appropriate decimal places for percentages
   */
  private determinePercentageDecimals(values: any[]): number {
    const numbers = values.filter(v => typeof v === 'number' || !isNaN(parseFloat(v)))
    
    if (numbers.length === 0) return 1
    
    // Check precision of existing values
    const maxDecimals = numbers.reduce((max, v) => {
      const num = typeof v === 'number' ? v : parseFloat(v)
      const decimals = (num.toString().split('.')[1] || '').length
      return Math.max(max, decimals)
    }, 0)
    
    return Math.min(maxDecimals, 2)
  }

  /**
   * Determine appropriate decimal places for regular numbers
   */
  private determineNumberDecimals(values: any[]): number {
    const numbers = values.filter(v => typeof v === 'number' || !isNaN(parseFloat(v)))
    
    if (numbers.length === 0) return 0
    
    // Check if any values have decimals
    const hasDecimals = numbers.some(v => {
      const num = typeof v === 'number' ? v : parseFloat(v)
      return num % 1 !== 0
    })
    
    return hasDecimals ? 2 : 0
  }

  /**
   * Main method to detect column type
   */
  async detectColumnType(columnName: string, sampleValues: any[]): Promise<ColumnType> {
    // Filter out null/undefined values
    const validValues = sampleValues.filter(v => v !== null && v !== undefined && v !== '')
    
    if (validValues.length === 0) {
      return { type: 'text', confidence: 0.1 }
    }
    
    // Use fuzzy logic first
    const fuzzyResult = this.detectTypeByKeywords(columnName, validValues)
    
    
    // If confidence is high enough, return fuzzy result
    if (fuzzyResult.confidence > 0.7) {
      return fuzzyResult
    }
    
    // TODO: Add LLM-based detection as fallback if enabled
    if (this.options.useLLM && fuzzyResult.confidence < 0.5) {
      // This would call an LLM API to analyze the column
      // For now, we'll use the fuzzy result
    }
    
    return fuzzyResult
  }

  /**
   * Format value based on detected type
   */
  formatValue(value: any, columnType: ColumnType): string {
    if (value === null || value === undefined || value === '') {
      return ''
    }

    switch (columnType.type) {
      case 'currency':
        return this.formatCurrency(value, columnType)
      
      case 'percentage':
        return this.formatPercentage(value, columnType)
      
      case 'number':
        return this.formatNumber(value, columnType)
      
      case 'date':
        return this.formatDate(value, columnType)
      
      case 'email':
      case 'phone':
      case 'text':
      default:
        return String(value)
    }
  }

  /**
   * Format currency values
   */
  private formatCurrency(value: any, columnType: ColumnType): string {
    const num = typeof value === 'number' ? value : parseFloat(value)
    
    if (isNaN(num)) return String(value)
    
    return new Intl.NumberFormat(columnType.locale || this.locale, {
      style: 'currency',
      currency: columnType.currency || this.currency,
      minimumFractionDigits: columnType.decimals || 0,
      maximumFractionDigits: columnType.decimals || 2
    }).format(num)
  }

  /**
   * Format percentage values
   */
  private formatPercentage(value: any, columnType: ColumnType): string {
    const num = typeof value === 'number' ? value : parseFloat(value)
    
    if (isNaN(num)) return String(value)
    
    // Assume values > 1 are already in percentage form, otherwise convert
    const percentValue = num > 1 ? num : num * 100
    
    return new Intl.NumberFormat(this.locale, {
      style: 'percent',
      minimumFractionDigits: columnType.decimals || 0,
      maximumFractionDigits: columnType.decimals || 2
    }).format(percentValue / 100)
  }

  /**
   * Format number values
   */
  private formatNumber(value: any, columnType: ColumnType): string {
    const num = typeof value === 'number' ? value : parseFloat(value)
    
    if (isNaN(num)) return String(value)
    
    return new Intl.NumberFormat(this.locale, {
      minimumFractionDigits: columnType.decimals || 0,
      maximumFractionDigits: columnType.decimals || 2
    }).format(num)
  }

  /**
   * Format date values
   */
  private formatDate(value: any, columnType?: ColumnType): string {
    // Handle null, undefined, empty values
    if (value === null || value === undefined || value === '') return String(value)
    
    const date = new Date(value)
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(value)
    }
    
    // Check if it's the Unix epoch (likely an error)
    if (date.getFullYear() === 1970 && date.getMonth() === 0 && date.getDate() === 1) {
      return String(value)
    }
    
    // Use custom format if specified
    const formatOptions = this.getDateFormatOptions(columnType?.dateFormat || 'short')
    
    return new Intl.DateTimeFormat(this.locale, formatOptions).format(date)
  }

  /**
   * Get date format options based on format key
   */
  private getDateFormatOptions(format: string): Intl.DateTimeFormatOptions {
    switch (format) {
      case 'long':
        return { year: 'numeric', month: 'long', day: 'numeric' }
      case 'numeric':
        return { year: 'numeric', month: 'numeric', day: 'numeric' }
      case 'iso':
        return { year: 'numeric', month: '2-digit', day: '2-digit' }
      case 'year-month':
        return { year: 'numeric', month: 'short' }
      case 'year-only':
        return { year: 'numeric' }
      case 'short':
      default:
        return { year: 'numeric', month: 'short', day: 'numeric' }
    }
  }
}

export default SmartFormatter